// PRD 10 §4.3 / §8.1 — content form validation. Mirrors the server-side checks in
// health-encyclopedia.mock so the user gets inline errors before a round-trip.
// Messages are i18n-keyed (`errors.{CODE}`) so KO/EN both resolve.
//
// The BLOCK rules are not defined here: PRD 10 v2.5/v2.6 unify the block editor across
// 건강백과 · 상담매뉴얼 · 관리지침 ("3개 콘텐츠 화면 블록 에디터 통일"), so they live once in
// `@/shared/utils/block-validation` and this file only maps a violation to 건강백과's copy.
// What stays here is what PRD 10 defines differently from PRD 11: the 100-char 제목 cap and
// the "본문 블록 필수 1개 이상" rule (§4.3).

import type { TFunction } from 'i18next';

import {
  BLOCK_DESC_MAX,
  IMAGE_ACCEPT,
  IMAGE_TITLE_MAX,
  MAX_BLOCKS,
  TEXT_BLOCK_MAX,
  VIDEO_TITLE_MAX,
  blockHasContent,
  blockHasEmoji,
  firstBlockRuleViolation,
  isAllowedVideoUrl,
  isBlockDescValid,
  isHttpsUrl,
  isImageFileValid,
  isImageTitleValid,
  isVideoTitleValid,
  plainTextLength,
  withoutEmptyMediaBlocks,
  type BlockRuleCode,
} from '@/shared/utils/block-validation';
import { hasEmoji } from '@/shared/utils/emoji';

import type { ContentBlock } from '../types/health-encyclopedia.types';

// [공통] 00 §8 — 건강백과 제목 1~100 (TITLE 클래스 일치).
export const TITLE_MAX = 100;

// Re-exported so this module stays the feature's single validation entry point and the
// screen components keep importing from one place.
export {
  BLOCK_DESC_MAX,
  IMAGE_ACCEPT,
  IMAGE_TITLE_MAX,
  MAX_BLOCKS,
  TEXT_BLOCK_MAX,
  VIDEO_TITLE_MAX,
  blockHasContent,
  blockHasEmoji,
  isBlockDescValid,
  isHttpsUrl,
  isImageFileValid,
  isImageTitleValid,
  isVideoTitleValid,
  plainTextLength,
  withoutEmptyMediaBlocks,
};

// A video URL is valid only when it is https AND a YouTube/Vimeo host (BE rejects anything
// else with CONTENT_URL_NOT_ALLOWED). The host rule lives in the shared video-embed module —
// the same one the previews embed with — so a URL this form accepts is always one the preview
// can render, and vice versa.
export const isVideoUrlValid = isAllowedVideoUrl;

export type ContentFormFieldKey = 'title' | 'categoryId' | 'blocks';

export interface ContentFormDraft {
  title: string;
  categoryId: number | null;
  blocks: ContentBlock[];
}

export type ContentFormErrors = Partial<Record<ContentFormFieldKey, string>>;

export function blocksHaveContent(blocks: ContentBlock[]): boolean {
  return blocks.some(blockHasContent);
}

// 건강백과's copy for each shared rule. VIDEO_URL_INVALID renders under this feature's
// existing `CONTENT_URL_NOT_ALLOWED` key (the BE error code it mirrors); EMPTY_BLOCK is
// unreachable here because §4.3's "1개 이상" check below fires first — see validateContentForm.
const BLOCK_ERROR_KEYS: Record<BlockRuleCode, string> = {
  TOO_MANY_BLOCKS: 'errors.TOO_MANY_BLOCKS',
  EMPTY_BLOCK: 'errors.EMPTY_BLOCK',
  TEXT_BLOCK_TOO_LONG: 'errors.TEXT_BLOCK_TOO_LONG',
  VIDEO_URL_INVALID: 'errors.CONTENT_URL_NOT_ALLOWED',
  VIDEO_TITLE_INVALID: 'errors.VIDEO_TITLE_INVALID',
  IMAGE_TITLE_INVALID: 'errors.IMAGE_TITLE_INVALID',
  BLOCK_DESC_TOO_LONG: 'errors.BLOCK_DESC_TOO_LONG',
  EMOJI_NOT_ALLOWED: 'common:errors.emojiNotAllowed',
};

export function validateContentForm(
  draft: ContentFormDraft,
  t: TFunction,
): { ok: boolean; errors: ContentFormErrors } {
  const errors: ContentFormErrors = {};

  const title = draft.title.trim();
  if (!title) errors.title = t('errors.TITLE_REQUIRED');
  else if (title.length > TITLE_MAX) errors.title = t('errors.TITLE_TOO_LONG');
  else if (hasEmoji(title)) errors.title = t('common:errors.emojiNotAllowed');

  if (draft.categoryId == null) errors.categoryId = t('errors.CATEGORY_REQUIRED');

  // PRD 10 v2.13 §4.3 — 본문 블록: 필수, **내용이 있는** 1개 이상. This is the one block rule
  // 건강백과 does NOT share with 상담매뉴얼 (which allows 0 blocks), so it stays here and runs FIRST.
  //
  // SHCT-662 — the answered CR. The check used to reject a content that merely CONTAINED an
  // empty block, so a filled text block next to an image block with no upload was refused with
  // "최소 1개 블록에 내용을 입력해주세요." — a message that names the one thing that was not wrong.
  // §3.7 now drops the unfilled media block at save and §8.1 limits that copy to 유효 블록 0개,
  // i.e. the body really is empty. Judge and submit the SAME filtered array.
  const blocks = withoutEmptyMediaBlocks(draft.blocks);
  if (!blocksHaveContent(blocks)) {
    errors.blocks = t('errors.CONTENT_REQUIRED');
  } else {
    const violation = firstBlockRuleViolation(blocks);
    if (violation) {
      errors.blocks = t(BLOCK_ERROR_KEYS[violation.code], { max: violation.max });
    }
  }

  return { ok: Object.keys(errors).length === 0, errors };
}
